import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useInView } from 'react-intersection-observer';

import { useIsFeatureEnabled } from 'tradera-hooks/use-is-feature-enabled';
import useIsMobile from 'tradera-hooks/use-is-mobile';
import { getImageSizes, getWebpImage } from '../image-sizes';
import { HEADER } from '../content-types';
import { trackContainerClick } from '../analytics';
import { isSpaLink, toRelativeTraderaUrl } from 'tradera-utils/url';
import ALink from 'tradera-components/alink/alink';
import MediaItem from '../media/media-item';
import TrendingSearchesTags from '../trending/trending-searches-tags';
import TraderaLogo from 'components/tradera/logo';
import { SearchInSpa, SearchOutsideSpa } from 'components/search/search';
import uiActions from 'tradera-state/ui/actions';
import RecentSearchesTags from '../recent-searches/recent-searches-tags';
import './start-page-hero.scss';

const StartPageHero = ({
    isLoading,
    showRecentSearches,
    showTrendingSearches
}) => {
    const {
        backgroundVideo,
        backgroundImage,
        isCampaignMode,
        trackingId,
        webpageUrl = ''
    } = useSelector(state => state.views.startPage.config.header);

    const inSPA = true;

    const videoEnabled = useIsFeatureEnabled('start-page-contentful-video');
    const video = videoEnabled ? backgroundVideo : null;

    const [searchElementRef, searchInView, searchInViewEntry] = useInView({
        rootMargin: '-45px 0px 0px 0px',
        threshold: 1.0
    });

    const dispatch = useDispatch();
    const toggleHeaderSearch = useCallback(
        show => dispatch(uiActions.toggleHeaderSearch(show)),
        [dispatch]
    );
    useEffect(() => {
        if (searchInViewEntry) {
            toggleHeaderSearch(!searchInView);
        } else if (isCampaignMode) {
            toggleHeaderSearch(true);
        }
    }, [searchInViewEntry, isCampaignMode, searchInView, toggleHeaderSearch]);

    const isMobile = useIsMobile();
    const imageSizes = getImageSizes(1, backgroundImage, HEADER);
    const webpImage = getWebpImage(1, backgroundImage, HEADER);
    const parsedCampaignUrl = isCampaignMode
        ? toRelativeTraderaUrl(webpageUrl)
        : null;

    return (
        <div className="startpage-hero" data-test-startpage-hero>
            {backgroundImage ? (
                <MediaItem
                    alternativeText="Start page cover image"
                    imageSizes={imageSizes}
                    image={backgroundImage}
                    video={video}
                    webpImage={webpImage}
                />
            ) : (
                <FallbackImage />
            )}
            {isCampaignMode ? (
                <ALink
                    useLink={inSPA && isSpaLink(parsedCampaignUrl)}
                    href={parsedCampaignUrl}
                    onClick={() => trackContainerClick(HEADER, trackingId)}
                    style={{ top: '0px' }}
                    className="position-absolute w-100 h-100"
                    aria-label={trackingId}
                />
            ) : (
                <div className="startpage-hero__content">
                    <div
                        className="container h-100 d-flex flex-column justify-content-center"
                        style={{
                            opacity: isLoading ? 0 : 1,
                            transition: 'opacity 200ms linear'
                        }}>
                        <div className="row">
                            <TraderaLogo
                                className={`startpage-hero__logo mx-auto mb-3 mb-sm-4 mb-md-5`}
                            />
                        </div>
                        <div className="row d-md-flex mb-2">
                            <div
                                id="start-page-hero-search"
                                className="col-10 offset-1 col-md-8 offset-md-2 col-lg-6 offset-lg-3"
                                ref={searchElementRef}>
                                {inSPA ? (
                                    <SearchInSpa
                                        inSPA={inSPA}
                                        inputClassName={
                                            isMobile ? '' : 'form-control-lg'
                                        }
                                    />
                                ) : (
                                    <SearchOutsideSpa
                                        inSPA={inSPA}
                                        inputClassName={
                                            isMobile ? '' : 'form-control-lg'
                                        }
                                    />
                                )}
                                {/* <Search
                                    inputClassName="form-control-lg"
                                    inSPA={inSPA}
                                /> */}
                            </div>
                        </div>
                        {showRecentSearches && (
                            <div className="row d-flex mb-2">
                                <div className="col-8 offset-2 col-lg-6 offset-lg-3">
                                    <RecentSearchesTags inHeader />
                                </div>
                            </div>
                        )}
                        {showTrendingSearches && (
                            <div className="row d-flex">
                                <div className="col-8 offset-2 col-lg-6 offset-lg-3 mt-1">
                                    <TrendingSearchesTags inHeader />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

StartPageHero.propTypes = {
    isLoading: PropTypes.bool.isRequired,
    showRecentSearches: PropTypes.bool.isRequired,
    showTrendingSearches: PropTypes.bool.isRequired
};

export default StartPageHero;

const FallbackImage = () => (
    <img
        className="startpage-hero__fallback-image"
        srcSet="/static/images/start-page/header-fallback-large.jpg 1920w, /static/images/start-page/header-fallback-medium.jpg 768w, /static/images/start-page/header-fallback-small.jpg 425w"
        sizes="(max-width: 425px) 425px, (max-width: 768px) 768px, 1920px"
        src="/static/images/start-page/header-fallback-large.jpg"
        alt="Start page cover image"
    />
);
